import React from "react";
import Decimal from "decimal.js";
import { useDocument } from "../loan_tools/loan_tool";
import CurrencyField from "../fields/currency_field";
import { dscr_total_monthly_rents } from "../../calculations/income";
import {
  loan_amount_first_mortgage,
  first_mortgage_payment_p_and_i,
  property_tax
} from "../../calculations/loan";
import {
  home_owners_insurance_monthly,
  flood_insurance_monthly,
  hoa_dues_1,
  hoa_dues_2,
  mello_roos
} from "../../calculations/property";
import { INTEREST_ONLY } from "../../constants/loan_payment_type";
import { dscrPropertyPatch, dscrLoanPatch, dscrAddressCszPatch, dscrHoaPropertyPatch } from "../../calculations/dscr";

function money(value) {
  return new Decimal(value || 0).toDecimalPlaces(0).toNumber().toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0
  });
}

function ratio(value) {
  return new Decimal(value || 0).toDecimalPlaces(3).toString();
}

// Subject-property carrying-cost components for one loan (monthly), sourced from
// the canonical fields (New Property / LE Price Eng) so they always match the app.
function subjectEscrowParts(documentType, loan, property) {
  const tax = property_tax(documentType, loan, property);
  const hoi = home_owners_insurance_monthly(documentType, property);
  const flood = flood_insurance_monthly(documentType, property);
  const hoa = hoa_dues_1(property, loan)
    .add(hoa_dues_2(property, loan))
    .add(mello_roos(property, loan));
  return { tax, hoi, flood, hoa, total: tax.add(hoi).add(flood).add(hoa) };
}

export default function DscrIncomeCalculator({ incomeSource, onChangeIncome }) {
  const [document, setDocument, { documentType }, actions] = useDocument();
  const { loans, property, property_to_be_sold, is_selling_property } = document;

  function saveNow() {
    try {
      actions.saveDocument();
    } catch (e) {
      // non-fatal
    }
  }
  // Two-way write-back: edit a line item here -> its SOURCE page (New Property /
  // Subject Property for escrows, LE Price Eng / Down Payment for rate/HOA/amount).
  function editProperty(field, value, save = true) {
    setDocument({ ...document, property: { ...property, ...dscrPropertyPatch(documentType, field, value) } });
    if (save) saveNow();
  }
  function editPropertyRaw(patch) {
    setDocument({ ...document, property: { ...property, ...patch } });
    saveNow();
  }
  function editLoan(loanIndex, field, value, save = true) {
    const nextLoans = loans.map((l, i) =>
      i === loanIndex ? { ...l, ...dscrLoanPatch(documentType, l, field, value) } : l
    );
    setDocument({ ...document, loans: nextLoans });
    if (save) saveNow();
  }
  // HOA writes both the loan and the subject property so it applies no matter which
  // one hoa_dues reads for this property type.
  function editHoa(loanIndex, value) {
    const nextLoans = loans.map((l, i) =>
      i === loanIndex ? { ...l, ...dscrLoanPatch(documentType, l, "hoa", value) } : l
    );
    setDocument({
      ...document,
      loans: nextLoans,
      property: { ...property, ...dscrHoaPropertyPatch(value) }
    });
    saveNow();
  }

  const rentRoll =
    incomeSource.dscr_rent_roll && incomeSource.dscr_rent_roll.length
      ? incomeSource.dscr_rent_roll
      : [{ notes: "", rent: "0" }];
  const totalRents = dscr_total_monthly_rents(incomeSource);

  // Property address is two-way with the SUBJECT property (New Property / Subject
  // Property page) — the true source. Editing here writes property.address / city /
  // state / zip, and edits made on those pages show here.
  const addrStreet = property.address || "";
  const addrCsz =
    [property.city, property.state].filter(Boolean).join(", ") +
    (property.zip ? " " + property.zip : "");

  function updateRoll(nextRoll) {
    onChangeIncome({ dscr_rent_roll: nextRoll });
  }
  function onChangeUnit(index, field, value) {
    const next = rentRoll.map((u, i) => (i === index ? { ...u, [field]: value } : u));
    updateRoll(next);
  }
  function addUnit() {
    updateRoll([...rentRoll, { notes: "", rent: "0" }]);
  }
  function removeUnit(index) {
    updateRoll(rentRoll.filter((_, i) => i !== index));
  }

  // One column per priced loan (a real scenario the LO can qualify against).
  const scenarios = (loans || [])
    .map((loan, index) => ({ loan, index }))
    .filter(({ loan }) => new Decimal(loan.rate || 0).gt(0))
    .map(({ loan, index }) => {
      // Best-effort: a half-built loan must never crash the Income page.
      try {
        const args = [documentType, loan, property, property_to_be_sold, is_selling_property];
        const loanAmount = loan_amount_first_mortgage(...args);
        const pi = first_mortgage_payment_p_and_i(...args);
        // Interest-only payment is computed unconditionally for the DSCR (loan ×
        // rate ÷ 12) so the ITIA DSCR is always shown, even on a P&I loan. Do NOT
        // use first_mortgage_payment_interest_only — that returns 0 unless the loan
        // itself is set to interest-only (the cause of the wrong 9.752 ITIA DSCR).
        const io = new Decimal(loanAmount)
          .mul(new Decimal(loan.rate || 0).div(100))
          .div(12);
        const parts = subjectEscrowParts(documentType, loan, property);
        const escrow = parts.total;
        const pitia = pi.add(escrow);
        const itia = io.add(escrow);
        const dscrPitia = pitia.gt(0) ? totalRents.div(pitia) : new Decimal(0);
        const dscrItia = itia.gt(0) ? totalRents.div(itia) : new Decimal(0);
        const interestOnly = loan.payment_type === INTEREST_ONLY;
        return { index, loan, loanAmount, pi, io, parts, escrow, pitia, itia, dscrPitia, dscrItia, interestOnly };
      } catch (e) {
        const z = new Decimal(0);
        return {
          index, loan, loanAmount: z, pi: z, io: z,
          parts: { tax: z, hoi: z, flood: z, hoa: z, total: z },
          escrow: z, pitia: z, itia: z,
          dscrPitia: z, dscrItia: z, interestOnly: loan.payment_type === INTEREST_ONLY
        };
      }
    });

  const belowStyle = { background: "#fdf3c7" }; // yellow flag when DSCR < 1.0
  function dscrCell(dscr) {
    const below = new Decimal(dscr).lt(1);
    return (
      <td className="right-align num" style={below ? belowStyle : undefined}>
        {ratio(dscr)}
      </td>
    );
  }

  return (
    <div className="edit-borrower-income-calculator dscr-income-calculator">
      <h3>NonQM DSCR — Rents Received</h3>
      <p className="dscr-note">
        The borrower qualifies on the property&apos;s rental cash flow. Enter the
        rent for each unit (100% of gross is used). The DSCR ratio below is the
        deciding factor — anything under 1.00 is highlighted.
      </p>

      <div className="dscr-address">
        <div className="dscr-address-row">
          <label>Property Address</label>
          <input
            key={"street-" + addrStreet}
            type="text"
            className="dscr-input"
            defaultValue={addrStreet}
            onBlur={e => editPropertyRaw({ address: e.target.value })}
            placeholder="Street address"
          />
        </div>
        <div className="dscr-address-row">
          <label>City, State, Zip</label>
          <input
            key={"csz-" + addrCsz}
            type="text"
            className="dscr-input"
            defaultValue={addrCsz}
            onBlur={e => editPropertyRaw(dscrAddressCszPatch(e.target.value))}
            placeholder="City, State  Zip"
          />
        </div>
      </div>

      <table className="dscr-rent-roll">
        <thead>
          <tr>
            <th className="dscr-unit-col">Unit</th>
            <th>Notes</th>
            <th className="right-align">Rent Received</th>
            <th className="no-print" />
          </tr>
        </thead>
        <tbody>
          {rentRoll.map((unit, index) => (
            <tr key={index}>
              <td className="dscr-unit-col">Rental Unit {index + 1}</td>
              <td>
                <input
                  type="text"
                  className="dscr-input"
                  value={unit.notes || ""}
                  onChange={e => onChangeUnit(index, "notes", e.target.value)}
                />
              </td>
              <td className="right-align">
                <CurrencyField
                  fieldName="rent"
                  value={unit.rent}
                  onChange={obj => onChangeUnit(index, "rent", obj.rent)}
                />
              </td>
              <td className="no-print">
                {rentRoll.length > 1 && (
                  <button className="button" onClick={() => removeUnit(index)}>
                    Remove
                  </button>
                )}
              </td>
            </tr>
          ))}
          <tr>
            <td className="bold" colSpan="2">Total Rents Received</td>
            <td className="right-align num bold">{money(totalRents)}</td>
            <td className="no-print">
              <button className="button" onClick={addUnit}>
                Add Unit
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <h3>Subject Property PITIA DSCR — by Loan</h3>
      {scenarios.length === 0 ? (
        <p className="dscr-note">
          Enter a rate on the LE Price Eng page for at least one loan to see the
          DSCR qualification.
        </p>
      ) : (
        <table className="dscr-results">
          <thead>
            <tr>
              <th />
              {scenarios.map(s => (
                <th key={s.index} className="right-align">
                  Loan {s.index + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Rate <span className="dscr-edit-hint">(editable)</span></td>
              {scenarios.map(s => (
                <td key={s.index} className="right-align">
                  <input
                    type="text"
                    className="dscr-input dscr-input-right"
                    value={s.loan.rate || ""}
                    onChange={e => editLoan(s.index, "rate", e.target.value, false)}
                    onBlur={saveNow}
                  />
                </td>
              ))}
            </tr>
            <tr>
              <td>Loan Amount (1st) <span className="dscr-edit-hint">(editable)</span></td>
              {scenarios.map(s => (
                <td key={s.index} className="right-align">
                  <CurrencyField
                    fieldName="v"
                    value={s.loanAmount.toString()}
                    onChange={obj => editLoan(s.index, "loanAmount", obj.v)}
                  />
                </td>
              ))}
            </tr>
            <tr>
              <td>1st Loan Payment (P&amp;I)</td>
              {scenarios.map(s => (
                <td key={s.index} className="right-align num">{money(s.pi)}</td>
              ))}
            </tr>
            <tr>
              <td>Interest Only Payment</td>
              {scenarios.map(s => (
                <td key={s.index} className="right-align num">{money(s.io)}</td>
              ))}
            </tr>
            <tr>
              <td>Property Tax <span className="dscr-edit-hint">(editable)</span></td>
              {scenarios.map(s => (
                <td key={s.index} className="right-align">
                  <CurrencyField
                    fieldName="v"
                    value={s.parts.tax.toString()}
                    onChange={obj => editProperty("tax", obj.v)}
                  />
                </td>
              ))}
            </tr>
            <tr>
              <td>Home Owners Insurance <span className="dscr-edit-hint">(editable)</span></td>
              {scenarios.map(s => (
                <td key={s.index} className="right-align">
                  <CurrencyField
                    fieldName="v"
                    value={s.parts.hoi.toString()}
                    onChange={obj => editProperty("hoi", obj.v)}
                  />
                </td>
              ))}
            </tr>
            <tr>
              <td>Flood Insurance <span className="dscr-edit-hint">(editable)</span></td>
              {scenarios.map(s => (
                <td key={s.index} className="right-align">
                  <CurrencyField
                    fieldName="v"
                    value={s.parts.flood.toString()}
                    onChange={obj => editProperty("flood", obj.v)}
                  />
                </td>
              ))}
            </tr>
            <tr>
              <td>HOA / Mello Roos / Other <span className="dscr-edit-hint">(editable)</span></td>
              {scenarios.map(s => (
                <td key={s.index} className="right-align">
                  <CurrencyField
                    fieldName="v"
                    value={s.parts.hoa.toString()}
                    onChange={obj => editHoa(s.index, obj.v)}
                  />
                </td>
              ))}
            </tr>
            <tr>
              <td className="bold">Total PITIA</td>
              {scenarios.map(s => (
                <td key={s.index} className="right-align num bold">{money(s.pitia)}</td>
              ))}
            </tr>
            <tr>
              <td className="bold">Total ITIA</td>
              {scenarios.map(s => (
                <td key={s.index} className="right-align num bold">{money(s.itia)}</td>
              ))}
            </tr>
            <tr>
              <td className="bold">Subject Property PITIA DSCR</td>
              {scenarios.map(s => (
                <React.Fragment key={s.index}>{dscrCell(s.dscrPitia)}</React.Fragment>
              ))}
            </tr>
            <tr>
              <td className="bold">Subject Property ITIA DSCR</td>
              {scenarios.map(s => (
                <React.Fragment key={s.index}>{dscrCell(s.dscrItia)}</React.Fragment>
              ))}
            </tr>
            <tr>
              <td>Interest Only?</td>
              {scenarios.map(s => (
                <td key={s.index} className="right-align">{s.interestOnly ? "Yes" : "No"}</td>
              ))}
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}
